/** The engine pages (Cash Signals, Sales Signals): the browser side of
 *  backend/chainlit/engine_pages.py and each page's own module.
 *
 *  The server writes every word on a page, in English like the rest of the
 *  product; a page is sections of the same typed blocks Know your data uses,
 *  plus, since the rich layout (2026-11-05), a strip of figures and, on Cash
 *  Signals, the customers table and the cheque lines.
 */
import { Lang, Section } from '@/components/know-your-data/api';

/** One figure in the strip at the top of a page. */
export interface Kpi {
  label: string;
  value: string;
  sub?: string | null;
  /** Today only: the page the figure comes from. */
  page?: string;
  /** Cash Signals only: 'ink' (the lead tile), 'bad', 'warn' or null. */
  tone?: 'ink' | 'bad' | 'warn' | null;
}

export interface CustomerSignal {
  kind: 'stopped' | 'growing' | 'low_rate' | 'paying_down' | 'steady';
  label: string;
}

export interface CashCustomer {
  name: string;
  owed: number;
  owed_display: string;
  ago: number | null;
  ago_display: string | null;
  last_payment: string;
  last_invoice: string;
  signal: CustomerSignal;
  asks: { label: string; question: string }[];
}

export interface CashCustomers {
  tabs: { id: string; label: string; count: number }[];
  rows: CashCustomer[];
  note: string;
}

export interface ChequeLines {
  as_of: string;
  lines: {
    kind: 'ok' | 'warn' | 'out' | 'bad';
    label: string;
    text: string;
    value: string;
  }[];
}

export interface EnginePage {
  building?: boolean;
  lang: Lang;
  dir: 'ltr' | 'rtl';
  languages: Lang[];
  title?: string;
  subtitle?: string;
  sections?: Section[];
  /** What the page says when it has no sections: no feeds, or not enough data. */
  empty?: string | null;
  /** Cash Signals: the rich layout's own data beside the sections. */
  kpis?: Kpi[];
  customers?: CashCustomers | null;
  cheques?: ChequeLines | null;
}

async function jsonFetch<T>(url: string): Promise<T> {
  const res = await fetch(url);
  if (!res.ok) {
    const err = await res
      .json()
      .catch(() => ({ detail: `HTTP ${res.status}` }));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

/** One engine page: the API path the Shell routes it to. */
export const fetchEnginePage = (api: string) => jsonFetch<EnginePage>(api);

/** Whether this user's company feeds anything the page reads: the page's
 *  section is in the navigation only then. */
export const fetchEngineStatus = (api: string) =>
  jsonFetch<{ available: boolean }>(`${api}/status`);

export interface TodayTile {
  engine: string;
  page: string;
  title: string;
  building: boolean;
  value: string | null;
  label: string | null;
  /** "was 17.5M on 7 Oct" once a week of history exists; null before. */
  change?: string | null;
}

export interface TodayItem {
  engine: string;
  page: string;
  section: string;
  value: number;
  display: string;
  title: string;
  text: string;
}

export interface TodayTop {
  name: string;
  display: string;
  signal: { kind: 'stopped' | 'growing'; label: string } | null;
}

export interface TodayPage {
  lang: Lang;
  dir: 'ltr' | 'rtl';
  title: string;
  subtitle?: string;
  /** "Good morning, Sobhy" and "Thursday 5 November" (rich layout). */
  greeting?: string;
  day?: string;
  tiles: TodayTile[];
  kpis?: Kpi[];
  top?: TodayTop[];
  items: TodayItem[];
  building: boolean;
  empty?: string | null;
}

export const fetchToday = () => jsonFetch<TodayPage>('/api/today');

export const CASH_API = '/api/cash-engine';
export const SALES_API = '/api/sales-engine';
export const MARGIN_API = '/api/margin-engine';
export const TODAY_API = '/api/today';
